import gql from 'graphql-tag';
import { OFFER_FRAGMENT, VENDOR_DATA_FRAGMENT } from 'core/fragments/offer';

export const GET_OFFER = gql`
  query getOffer($offerId: ID!) {
    offer: getOffer(id: $offerId) {
      ...OfferFragment
    }
  }
  ${OFFER_FRAGMENT}
`;

// Separate query for vendor data — restricted to TeamLeaders in the schema.
// Fetched independently so non-TeamLeader offer queries don't trigger auth errors.
export const GET_OFFER_VENDOR_DATA = gql`
  query getOfferVendorData($offerId: ID!) {
    offer: getOffer(id: $offerId) {
      id
      ...VendorDataFragment
    }
  }
  ${VENDOR_DATA_FRAGMENT}
`;

export const GET_EXISTING_CUSTOMER_POLICIES = gql`
  query getExistingCustomerPolicies($id: ID!) {
    account: getAccount(id: $id) {
      policies {
        id
        policyType
        state
        effectiveDate
        endDate
        fullTermPolicyEndDate
        fullTermCancel
        offer {
          ...OfferFragment
        }
        segments {
          segmentId
          startDate
          drivers {
            id
            isPrimary
            dateOfBirth
            firstName
            lastName
            excludeDriver
            hasViolationsBlockingUmbrella
            hasMajorViolationBlockingUmbrella
            hasAtFaultAccidentBlockingUmbrella
            insuranceScore {
              autoTotal
              homeTotal
            }
            insuranceScoreOverride
            insuranceScoreOverrideValue
          }
          cars {
            VIN
          }
          global {
            affinity
            continuousAutoCoverageStartDate
          }
          autoCoverage {
            policyLimitBIPD
            policyLimitUMBI
            policyLimitUIMBI
          }
          homeCoverage {
            coverageX
          }
          condoCoverage {
            coverageX
          }
        }
      }
    }
  }
  ${OFFER_FRAGMENT}
`;

export const RECALCULATE_QUOTE_TO_CLUSTER = gql`
  query recalculateQuoteToCluster($offerId: ID!, $revisedQuoteDetails: QuoteDetailsInput!) {
    cluster: recalculateQuoteToCluster(offerId: $offerId, revisedQuoteDetails: $revisedQuoteDetails) {
      id
      offers {
        ...OfferFragment
      }
    }
  }
  ${OFFER_FRAGMENT}
`;

export const UW_APPROVE_FOR_CHECKOUT = gql`
  mutation uwApproveForCheckout($offerId: ID!, $uwApprovalDetails: UWApprovalDetailsInput!) {
    cluster: uwApproveForCheckout(offerId: $offerId, uwApprovalDetails: $uwApprovalDetails) {
      id
      offers {
        ...OfferFragment
      }
    }
  }
  ${OFFER_FRAGMENT}
`;

export const REQUEST_BIND = gql`
  mutation ($request: BindRequestInput!) {
    requestBind(request: $request) {
      id
      systemId
    }
  }
`;

export const ADD_REFERRAL = gql`
  mutation addReferral($referralDetails: ReferralDetailsInput) {
    friendBuyLink: addReferral(referralDetails: $referralDetails)
  }
`;

export const DOWNLOAD_QUOTE = gql`
  mutation generateApplicationDraft($details: QuotePdfInput) {
    quotePdf: generateApplicationDraft(details: $details)
  }
`;

export const DOWNLOAD_OFFER = gql`
  query getOfferPdf($documentUrl: String!, $documentId: String!, $bypassCache: Boolean) {
    offerPdf: getDocraptorDocument(documentUrl: $documentUrl, documentId: $documentId, bypassCache: $bypassCache)
  }
`;

export const GET_POLICY_FROM_OFFER_ID = gql`
  query ($offerId: ID!, $includeLineage: Boolean) {
    getPolicyFromOfferId(offerId: $offerId, includeLineage: $includeLineage) {
      accountId
      id
      # #35936: distinguishes an EXACT match (policy.offerId === the open offer id → bound from this
      # exact quote, hard block) from a FUZZY member+location match (different/unknown quote → warn).
      offerId
    }
  }
`;

export const ADD_DRIVERS_CARS_AND_RECALCULATE = gql`
  mutation addDriversAndCarsAndRecalculate(
    $offerId: ID!
    $newDrivers: [PersonDetailsInput]
    $newVins: [CarDetailsInput]
    $newTrailerVins: [TrailerDetailsInput]
    $revisedQuoteDetails: QuoteDetailsInput
  ) {
    cluster: addCarsAddDriversAndRecalculateQuoteToCluster(
      offerId: $offerId
      newDrivers: $newDrivers
      newVins: $newVins
      newTrailerVins: $newTrailerVins
      revisedQuoteDetails: $revisedQuoteDetails
    ) {
      id
      offers {
        ...OfferFragment
      }
    }
  }
  ${OFFER_FRAGMENT}
`;

export const ADD_TO_PRE_BIND_UW_REJECTIONS = gql`
  mutation ($webUserId: ID!, $ineligibleForHome: Boolean!, $ineligibleForAuto: Boolean!, $attestationAgent: String!) {
    addToPreBindUWRejections(
      webUserId: $webUserId
      ineligibleForHome: $ineligibleForHome
      ineligibleForAuto: $ineligibleForAuto
      attestationAgent: $attestationAgent
    ) {
      webUserId
      ineligibleForHome
      ineligibleForAuto
      attestationAgent
    }
  }
`;

export const GET_PRE_BIND_UW_REJECTIONS_BY_WEB_USER_ID = gql`
  query ($webUserId: ID!) {
    getPreBindUWRejectionsByWebUserId(webUserId: $webUserId) {
      webUserId
      ineligibleForHome
      ineligibleForAuto
    }
  }
`;

export const GET_CAR_DRIVER_SUGGESTIONS = gql`
  query getCarDriverSuggestions($offerId: ID!) {
    driverSuggestions: getDriverSuggestions(offerId: $offerId) {
      id
      firstName
      lastName
      birthDate
      gender
      maritalStatus
      driversLicenseState
      ageFirstLicensed
      age
    }

    carSuggestions: getCarSuggestions(offerId: $offerId) {
      VIN
      year
      make
      model
    }
  }
`;

export const CLEAR_REJECT_CODES = gql`
  mutation clearRejectCodes($offerId: ID!, $invokedFromStaff: Boolean) {
    offer: clearRejectCodes(offerId: $offerId, invokedFromStaff: $invokedFromStaff) {
      ...OfferFragment
    }
  }
  ${OFFER_FRAGMENT}
`;

export const SWAP_HOME_RENTERS = gql`
  query swapHomeRentersPolicy($offerId: ID!, $revisedQuoteDetails: QuoteDetailsInput!) {
    offer: swapHomeRentersPolicy(offerId: $offerId, revisedQuoteDetails: $revisedQuoteDetails) {
      ...OfferFragment
    }
  }
  ${OFFER_FRAGMENT}
`;

export const FILL_OUT_SKIPPED_DATA_PULLS = gql`
  query fillOutSkippedDataPullsRecalculateQuoteToCluster(
    $offerId: ID!
    $invokedFromStaff: Boolean
    $dataPulls: [String]
  ) {
    cluster: fillOutSkippedDataPullsRecalculateQuoteToCluster(
      offerId: $offerId
      invokedFromStaff: $invokedFromStaff
      dataPulls: $dataPulls
    ) {
      id
      offers {
        ...OfferFragment
      }
    }
  }
  ${OFFER_FRAGMENT}
`;

export const SEND_PURCHASE_LINK = gql`
  mutation sendPurchaseLink($webUserId: ID!, $offerId: ID!, $purchaseInformation: PurchaseInformationInput!) {
    offer: sendPurchaseLink(webUserId: $webUserId, offerId: $offerId, purchaseInformation: $purchaseInformation) {
      ...OfferFragment
    }
  }
  ${OFFER_FRAGMENT}
`;

export const SEND_OFFER_LINK_TO_REP = gql`
  mutation sendOfferLinkToRep($args: SendOfferLinkToRepInput!) {
    sendOfferLinkToRep(args: $args) {
      success
      message
    }
  }
`;

export const GET_MERGED_OFFER = gql`
  query getMergedOffer($offerId: ID!, $rater: String) {
    offer: getMergedOffer(offerId: $offerId, rater: $rater) {
      id
      usingNonVerifiedData
    }
  }
`;

// `optionTypes` carries the policy types the offer being viewed is quoting, so the backend can
// refuse to redirect onto a different product (#34790). Optional server-side, so an older bundle
// keeps working.
export const GET_MOST_RECENT_STAFF_EDITED_OFFER = gql`
  query getMostRecentStaffEditedOffer($webUserId: String!, $optionTypes: [String!]) {
    getMostRecentStaffEditedOffer(webUserId: $webUserId, optionTypes: $optionTypes) {
      offerId
      updatedDateTime
    }
  }
`;

export const GET_OFFER_DIFFERENCE = gql`
  query getOfferDifference($oldOfferId: ID!, $newOfferId: ID!) {
    getOfferDifference(oldOfferId: $oldOfferId, newOfferId: $newOfferId) {
      success
      difference {
        addedChanges
        removedChanges
        diffKeys {
          full
          filtered
        }
        diff {
          full {
            addedChanges
            removedChanges
          }
          filtered {
            addedChanges
            removedChanges
          }
        }
        eligibilityChanges {
          policyType
          wasEligible
          isEligible
          rejectionCodesAdded
          rejectionCodesRemoved
        }
      }
      error
    }
  }
`;

export const UPDATE_OFFER_ADVANCED_CONFIGURATION = gql`
  mutation updateOfferAdvancedConfiguration($offerId: ID!, $skipUnderwritingUntil: AWSDateTime) {
    updateOfferAdvancedConfiguration(offerId: $offerId, skipUnderwritingUntil: $skipUnderwritingUntil) {
      success
      message
    }
  }
`;

export const SEND_TO_GAYA = gql`
  mutation sendToGaya($id: String!) {
    sendToGaya(id: $id) {
      success
      message
      entitiesSent
      details
    }
  }
`;

export const SEND_OFFER_FOR_UNDERWRITING_APPROVAL = gql`
  mutation sendOfferForUnderwritingApproval(
    $offerId: ID!
    $sentToUnderwritingAt: AWSDateTime!
    $sentToUnderwritingBy: String!
    $acknowledgeExistingActivePolicy: Boolean
  ) {
    sendOfferForUnderwritingApproval(
      args: {
        offerId: $offerId
        sentToUnderwritingAt: $sentToUnderwritingAt
        sentToUnderwritingBy: $sentToUnderwritingBy
        acknowledgeExistingActivePolicy: $acknowledgeExistingActivePolicy
      }
    ) {
      id
      offers {
        ...OfferFragment
      }
    }
  }
  ${OFFER_FRAGMENT}
`;

export const GET_SPIN_THE_WHEEL_ELIGIBILITY = gql`
  query getSpinTheWheelEligibility($email: String!) {
    getSpinTheWheelEligibility(email: $email) {
      eligible
    }
  }
`;

export const GET_SPIN_THE_WHEEL_HISTORY = gql`
  query getSpinTheWheelHistory($rep: String!) {
    getSpinTheWheelHistory(rep: $rep) {
      hasSpunBefore
    }
  }
`;

export const GET_SPIN_THE_WHEEL_RESULT = gql`
  query getSpinTheWheelResult($offerId: ID!) {
    getSpinTheWheelResult(offerId: $offerId) {
      offerId
      amount
      createdAt
    }
  }
`;

export const GET_SPIN_THE_WHEEL_CONFIG = gql`
  query getSpinTheWheelConfig($offerId: ID!, $isBundle: Boolean!) {
    getSpinTheWheelConfig(offerId: $offerId, isBundle: $isBundle) {
      wheelVariant
      amounts
      bundleMaxAmount
    }
  }
`;

export const SUBMIT_SPIN_RESULT = gql`
  mutation submitSpinResult($offerId: ID!, $isBundle: Boolean!) {
    submitSpinResult(offerId: $offerId, isBundle: $isBundle) {
      offerId
      amount
      tremendousOrderId
      status
    }
  }
`;

export const GET_RATING_WORKSHEET = gql`
  query getRatingWorksheet($clarionDoorIds: [String!]!, $clarionDoorRelease: String) {
    getRatingWorksheet(clarionDoorIds: $clarionDoorIds, clarionDoorRelease: $clarionDoorRelease)
  }
`;

// Shown when a farm offer/segment can't be located for worksheet download — it was rated before the
// release was persisted per-offer AND lives on a release-pinned ClarionDoor instance (e.g. CFM), so
// the user must re-rate (#33862). Records on the production instance download without a re-rate. The
// backend (get-rating-worksheet lambda) decides and returns this exact message when a re-rate is
// required; kept in sync with WORKSHEET_RERATE_MESSAGE there.
export const WORKSHEET_RERATE_MESSAGE =
  'This offer was rated on a prior version of the rater. Re-rate the offer to download its rating worksheet.';

// True when a getRatingWorksheet failure is the backend's re-rate guard (vs a generic error), so the
// UI can show the actionable re-rate copy. The message surfaces via the ApolloError's graphQLErrors.
export const isRerateError = (err) => {
  if (!err) {
    return false;
  }
  const messages = [err.message, ...(err.graphQLErrors?.map((e) => e?.message) ?? [])].filter(Boolean);
  return messages.some((message) => message.includes(WORKSHEET_RERATE_MESSAGE));
};
